import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { MessageDialogService } from '@service/message-dialog.service';
import { OutputSettingsSocketService } from '@service/output-settings-socket.service';
import { OutputSettingsService } from '@service/output-settings.service';
import { OutputSetting, SignalType, SourceChannelCondition } from 'icreate/common/models/output-settings';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { terminalType } from 'icreate/common/models/slot';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogType,
    ConfirmationDialogOption,
} from '@components/confirmation-dialog/confirmation-dialog.component';

enum FilterCondition {
    UNDEFINED = 'undefined',
    EMPTY = 'empty',
    ACTIVE = 'active',
    ALARM = 'alarm',
    ON_OFF = 'on-off',
}

@Component({
    selector: 'app-digital-output-settings-table',
    templateUrl: './digital-output-settings-table.component.html',
    styleUrl: './digital-output-settings-table.component.css',
    standalone: false,
})
export class DigitalOutputSettingsTableComponent implements OnInit, OnChanges, OnDestroy {
    readonly maxPageDisplay = 10;
    readonly FILTER_EMTPY = FilterCondition.EMPTY;
    readonly FILTER_ACTIVE = FilterCondition.ACTIVE;
    readonly FILTER_ALARM = FilterCondition.ALARM;
    readonly FILTER_ON_OFF = FilterCondition.ON_OFF;
    readonly FILTER_UNDEFINED = FilterCondition.UNDEFINED;

    @Input() selectedSlot: SensorTagSlot;
    @Input() vessel: Vessel;
    @Output() onSettingSelected: EventEmitter<OutputSetting> = new EventEmitter();
    outputSettings: OutputSetting[];
    displaySettings: OutputSetting[];
    private filteredSettings: OutputSetting[];
    isLoadingTerminals: boolean = false;
    showOptions: boolean = false;
    showFilterOptions: boolean = false;
    filterCondition: FilterCondition = FilterCondition.UNDEFINED;

    currentPage: number = 0;
    maxNumPages: number = 0;

    private destroy$: Subject<void> = new Subject();

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        this.showOptions = false;
        this.showFilterOptions = false;
    }

    constructor(
        private translate: TranslateService,
        private outputSettingsService: OutputSettingsService,
        private outputSettingsSocket: OutputSettingsSocketService,
        private messageDialogService: MessageDialogService,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.outputSettingsSocket
            .digitalSettingsUpdated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ setting, vesselId }: { setting: OutputSetting; vesselId: string }) => {
                if (this.vessel.id == vesselId) {
                    if (setting.slotId && setting.slotId == this.selectedSlot.slotId) {
                        this.outputSettings = this.outputSettings.map(outputSetting => {
                            if (outputSetting.terminalNum == setting.terminalNum) return setting;
                            return outputSetting;
                        });
                        this.showUpdatedMessage(setting);
                    }
                }
            });

        this.outputSettingsSocket
            .digitalSettingsDeleted$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ slotId, terminalNums, vesselId }) => {
                if (this.vessel.id == vesselId) {
                    if (slotId == this.selectedSlot.slotId) {
                        this.outputSettings = this.outputSettings.map(outputSetting => {
                            if (terminalNums.includes(outputSetting.terminalNum)) {
                                outputSetting.channels = [];
                                outputSetting.sourceChannelCondition = null;
                                outputSetting.outputMode = null;
                                outputSetting.signalType = null;
                            }
                            return outputSetting;
                        });
                        if (terminalNums && terminalNums.length > 1) {
                            this.showDeletedSingleMessage(terminalNums[0]);
                        } else if (terminalNums) this.showDeletedMessage();
                    }
                }
            });
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes.selectedSlot && this.selectedSlot) {
            this.isLoadingTerminals = true;
            try {
                const settings = await this.outputSettingsService.getAllDOTerminals(this.selectedSlot.slotId);
                const settingsMap = new Map<number, OutputSetting>();
                settings.forEach(setting => settingsMap.set(setting.terminalNum, setting));
                const numTerminals = this.getNumTerminals();
                this.outputSettings = Array.from({ length: numTerminals }, (v, i) => {
                    const terminalNum = i + 1;
                    if (settingsMap.has(terminalNum)) return settingsMap.get(terminalNum);
                    return {
                        vesselId: this.selectedSlot.vesselId,
                        slotId: this.selectedSlot.slotId,
                        terminalNum: i + 1,
                        channels: [],
                    };
                });
                this.updateFiltredSettings();
            } catch (err) {
                console.error(err);
            }
            this.isLoadingTerminals = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    filterSettings(filterOption?: FilterCondition) {
        let settings = this.outputSettings || [];
        if (filterOption && filterOption !== FilterCondition.UNDEFINED) {
            if (filterOption == FilterCondition.ACTIVE) {
                settings = this.outputSettings.filter(setting => setting.channels[0]);
            } else if (filterOption == FilterCondition.EMPTY) {
                settings = this.outputSettings.filter(setting => !setting.channels[0]);
            } else if (filterOption == FilterCondition.ALARM) {
                settings = this.outputSettings.filter(setting => setting.signalType == SignalType.ALARM);
            } else if (filterOption == FilterCondition.ON_OFF) {
                settings = this.outputSettings.filter(setting => setting.signalType == SignalType.ON_OFF);
            }
        }

        this.maxNumPages = Math.floor(settings.length / this.maxPageDisplay);

        this.filteredSettings = settings;
    }

    updateFilter(event, condition: FilterCondition) {
        if (condition == this.filterCondition) {
            this.filterCondition = null;
        } else this.filterCondition = condition;
        this.updateFiltredSettings(this.filterCondition);
    }

    updateDisplayedSettings() {
        this.displaySettings = this.filteredSettings.slice(
            this.currentPage * this.maxPageDisplay,
            (this.currentPage + 1) * this.maxPageDisplay
        );
    }

    updateFiltredSettings(filterOption?: FilterCondition) {
        this.filterSettings(filterOption);
        this.updateDisplayedSettings();
    }

    getNumTerminals() {
        if (!this.selectedSlot) return;
        let numTerminals = 0;
        this.selectedSlot.terminal.forEach(terminal => {
            if (terminal.type == terminalType.normal) numTerminals += 32;
            else if ((terminal.type = terminalType.relay)) numTerminals += 16;
        });
        return numTerminals;
    }

    getActiveTerminalsText() {
        if (!this.selectedSlot || this.isLoadingTerminals) return;
        const numTerminals = this.getNumTerminals();
        return this.translate.instant('output-settings.active-terminals', {
            active: `0/${numTerminals}`,
        });
    }

    getSlotTileArray() {
        return this.outputSettingsService.getSlotTileArray(this.selectedSlot);
    }

    getStatusString(setting: OutputSetting) {
        if (setting.channels[0]) return 'output-settings.AO.table.status.active';
        else return 'output-settings.AO.table.status.empty';
    }

    getChannelText(setting: OutputSetting) {
        if (setting.channels.length == 0) return '-';
        const firstChannel = setting.channels[0].channel;
        if (setting.channels.length == 1) return `${firstChannel.fcuData.fcuChannelId} ${firstChannel.opsData.name}`;
        let optionString;
        if (setting.sourceChannelCondition == SourceChannelCondition.AND) {
            optionString = this.translate.instant('output-settings.DO.dialog.output.source-condition.and');
        } else if (setting.sourceChannelCondition == SourceChannelCondition.OR) {
            optionString = this.translate.instant('output-settings.DO.dialog.output.source-condition.or');
        }

        const multipleChannelString = this.translate.instant('output-settings.DO.table.multiple-channels', {
            num: setting.channels.length,
        });
        return `${multipleChannelString} (${optionString})`;
    }

    getOutputType(setting: OutputSetting) {
        if (!setting.signalType || setting.signalType == SignalType.UNDEFINED) return '-';
        else if (setting.signalType == SignalType.ALARM) {
            return this.translate.instant('output-settings.DO.table.signal.alarm');
        } else if (setting.signalType == SignalType.ON_OFF) {
            return this.translate.instant('output-settings.DO.table.signal.on-off');
        } else if (setting.signalType == SignalType.ONE_SHOT) {
            return this.translate.instant('output-settings.DO.table.signal.one-shot');
        }
    }

    movePage(movement: -1 | 1) {
        if (movement == -1) {
            if (this.currentPage == 0) return;
            this.currentPage -= 1;
        } else {
            if (this.currentPage == this.maxNumPages) return;
            this.currentPage += 1;
        }
        this.updateDisplayedSettings();
    }

    getFooterText() {
        const numTerminals = this.filteredSettings?.length || 0;
        let translatedFooter;
        if (this.filterCondition) {
            translatedFooter = this.translate.instant('output-settings.DO.table.footer.filtered', {
                num: numTerminals,
            });
        } else {
            translatedFooter = this.translate.instant('output-settings.DO.table.footer', {
                num: numTerminals,
            });
        }

        const firstTerminal = this.currentPage * this.maxPageDisplay + 1;
        const lastTerminal = Math.min(this.currentPage * this.maxPageDisplay + this.maxPageDisplay, numTerminals);

        return `${firstTerminal}-${lastTerminal} ${translatedFooter}`;
    }

    async deleteAllChannels() {
        if (this.outputSettings.filter(setting => setting.channels?.length > 0)?.length == 0) {
            return;
        }
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: 'output-settings.DO.delete-all.title',
                message: 'output-settings.DO.delete-all.message',
                message2: 'output-settings.DO.delete-all.message-2',
                discardButton: 'output-settings.DO.delete-all.cancel',
                saveButton: 'output-settings.DO.delete-all.confirm',
                width: '452px',
            },
        });

        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) {
            try {
                this.isLoadingTerminals = true;
                if (!this.filteredSettings || this.filteredSettings.length == 0) return;
                const currentTerminalsSet = new Set<number>();
                this.filteredSettings.forEach(setting => currentTerminalsSet.add(setting.terminalNum));
                await this.outputSettingsService.deleteDOTerminals(
                    this.selectedSlot.slotId,
                    [...currentTerminalsSet],
                    this.vessel.id
                );
                this.outputSettings = this.outputSettings.map(setting => {
                    if (currentTerminalsSet.has(setting.terminalNum)) {
                        setting.channels = [];
                        setting.sourceChannelCondition = null;
                        setting.outputMode = null;
                        setting.signalType = null;
                    }
                    return setting;
                });
            } catch (err) {
                console.error(err);
            } finally {
                this.isLoadingTerminals = false;
            }
        }
    }

    openSetting(setting: OutputSetting) {
        this.onSettingSelected.emit(setting);
    }

    showUpdatedMessage(setting: OutputSetting) {
        this.messageDialogService.openMessage('output-settings.DO.notification.updated', {
            num: this.outputSettingsService.paddNumber(setting.terminalNum),
        });
    }

    showDeletedMessage() {
        this.messageDialogService.openMessage('output-settings.DO.notification.updated-slot', {
            num: this.outputSettingsService.paddNumber(this.selectedSlot.no),
        });
    }

    showDeletedSingleMessage(terminalNum) {
        this.messageDialogService.openMessage('output-settings.DO.notification.deleted', {
            num: this.outputSettingsService.paddNumber(terminalNum),
        });
    }
}
